import type { CookieConsentConfig } from 'vanilla-cookieconsent'

export const GA_MEASUREMENT_ID = 'G-QYQLJ0BKZ0'

function updateGtagConsent(acceptedCategories: string[]): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('consent', 'update', {
    analytics_storage: acceptedCategories.includes('analytics') ? 'granted' : 'denied',
  })
}

export const cookieConsentConfig: CookieConsentConfig = {
  root: 'body',
  autoShow: true,
  disablePageInteraction: false,
  hideFromBots: true,

  cookie: {
    name: 'lm_cc',
    expiresAfterDays: 365,
    sameSite: 'Lax',
  },

  guiOptions: {
    consentModal: {
      layout: 'box inline',
      position: 'bottom right',
      flipButtons: false,
      // Primary CTA ('Alle akzeptieren') gets filled brand-blue styling;
      // secondary + tertiary fall back to outlined / text-link styles.
      equalWeightButtons: false,
    },
    preferencesModal: {
      layout: 'box',
      position: 'right',
      flipButtons: false,
      equalWeightButtons: true,
    },
  },

  categories: {
    necessary: {
      enabled: true,
      readOnly: true,
    },
    analytics: {
      autoClear: {
        cookies: [
          { name: /^_ga/ },
          { name: '_gid' },
        ],
      },
      services: {
        ga4: {
          label: 'Google Analytics 4',
        },
      },
    },
  },

  onFirstConsent: ({ cookie }) => {
    updateGtagConsent(cookie.categories)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('lm:cookie-consent-given'))
    }
  },
  onConsent: ({ cookie }) => {
    updateGtagConsent(cookie.categories)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('lm:cookie-consent-given'))
    }
  },
  onChange: ({ cookie }) => {
    updateGtagConsent(cookie.categories)
  },

  language: {
    default: 'de',
    translations: {
      de: {
        consentModal: {
          title: 'Wir verwenden Cookies',
          description:
            'Wir nutzen technisch notwendige Cookies, damit unsere Website funktioniert. Mit Ihrer Zustimmung setzen wir zusätzlich Analyse-Cookies ein, um zu verstehen, wie unsere Website genutzt wird und sie laufend zu verbessern.',
          acceptAllBtn: 'Alle akzeptieren',
          acceptNecessaryBtn: 'Nur notwendige',
          showPreferencesBtn: 'Einstellungen',
        },
        preferencesModal: {
          title: 'Cookie-Einstellungen',
          acceptAllBtn: 'Alle akzeptieren',
          acceptNecessaryBtn: 'Nur notwendige',
          savePreferencesBtn: 'Auswahl speichern',
          closeIconLabel: 'Schliessen',
          sections: [
            {
              title: 'Cookie-Verwendung',
              description:
                'Wir verwenden Cookies, um grundlegende Funktionen der Website sicherzustellen und das Nutzererlebnis zu verbessern. Sie können für jede Kategorie selbst entscheiden, ob Sie diese zulassen möchten. Weitere Informationen finden Sie in unserer <a href="/datenschutz" class="cc__link">Datenschutzerklärung</a>.',
            },
            {
              title: 'Notwendige Cookies',
              description:
                'Diese Cookies sind für den Betrieb der Website zwingend erforderlich und können nicht deaktiviert werden. Sie werden in der Regel nur in Reaktion auf Aktionen von Ihnen gesetzt, etwa bei der Verwaltung Ihrer Datenschutz-Einstellungen oder beim Ausfüllen von Formularen.',
              linkedCategory: 'necessary',
            },
            {
              title: 'Analyse-Cookies',
              description:
                'Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren. Alle Informationen werden anonymisiert erfasst und ausgewertet.',
              linkedCategory: 'analytics',
              cookieTable: {
                headers: {
                  name: 'Name',
                  domain: 'Domain',
                  description: 'Beschreibung',
                  expiration: 'Speicherdauer',
                },
                body: [
                  {
                    name: '_ga',
                    domain: 'luechinger-metallbau.ch',
                    description: 'Google Analytics – unterscheidet einzelne Nutzer:innen.',
                    expiration: '2 Jahre',
                  },
                  {
                    name: '_ga_*',
                    domain: 'luechinger-metallbau.ch',
                    description: 'Google Analytics – speichert den Sitzungsstatus.',
                    expiration: '2 Jahre',
                  },
                ],
              },
            },
            {
              title: 'Weitere Informationen',
              description:
                'Bei Fragen zu unseren Cookie-Richtlinien und Ihren Auswahlmöglichkeiten kontaktieren Sie uns bitte über das <a href="/kontakt" class="cc__link">Kontaktformular</a>.',
            },
          ],
        },
      },
    },
  },
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}
